import { useId } from 'react';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';

import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent
} from '../../base/chart';

interface NavioAreaChartProps {
  data: {
    label: string;
    value: number;
    comparisonValue?: number;
  }[];
  className?: string;
  valueLabel?: string;
  /** Names the `comparisonValue` series; without it the comparison is not drawn. */
  comparisonLabel?: string;
  /** Formats x-axis ticks and the tooltip title from a point's `label`. */
  labelFormatter?: (label: string) => string;
  /** Formats y-axis ticks and the tooltip values. */
  valueFormatter?: (value: number) => string;
}

export function NavioAreaChart({
  data,
  className,
  valueLabel = 'Value',
  comparisonLabel,
  labelFormatter,
  valueFormatter
}: NavioAreaChartProps) {
  // useId() contains colons, which break `url(#...)` references in SVG.
  const gradientId = `area-fill-${useId().replace(/[^a-zA-Z0-9-]/g, '')}`;
  const chartConfig = {
    value: { label: valueLabel, color: 'var(--chart-1)' },
    comparisonValue: { label: comparisonLabel, color: 'var(--chart-2)' }
  } satisfies ChartConfig;

  return (
    <ChartContainer config={chartConfig} className={className}>
      <AreaChart accessibilityLayer data={data} margin={{ left: 0, right: 8, top: 8 }}>
        <defs>
          <linearGradient id={gradientId} x1='0' y1='0' x2='0' y2='1'>
            <stop offset='5%' stopColor='var(--color-value)' stopOpacity={0.5} />
            <stop offset='95%' stopColor='var(--color-value)' stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} strokeDasharray='3 3' />
        <XAxis
          dataKey='label'
          tickLine={false}
          axisLine={false}
          tickMargin={10}
          minTickGap={32}
          tickFormatter={labelFormatter}
        />
        <YAxis tickLine={false} axisLine={false} width={64} tickFormatter={valueFormatter} />
        <ChartTooltip
          cursor={false}
          content={
            <ChartTooltipContent
              labelFormatter={(_, payload) => {
                const label = String(payload[0]?.payload.label ?? '');
                return labelFormatter ? labelFormatter(label) : label;
              }}
              formatter={(value, name) => (
                <div className='flex w-full justify-between gap-4'>
                  <span className='text-muted-foreground'>
                    {name === 'comparisonValue' ? comparisonLabel : valueLabel}
                  </span>
                  <span className='font-medium tabular-nums'>
                    {valueFormatter ? valueFormatter(Number(value)) : Number(value).toLocaleString()}
                  </span>
                </div>
              )}
            />
          }
        />
        {comparisonLabel && <ChartLegend content={<ChartLegendContent />} />}
        {comparisonLabel && (
          <Area
            dataKey='comparisonValue'
            type='monotone'
            stroke='var(--color-comparisonValue)'
            strokeWidth={2}
            strokeDasharray='4 4'
            fill='none'
          />
        )}
        <Area
          dataKey='value'
          type='monotone'
          stroke='var(--color-value)'
          strokeWidth={2}
          fill={`url(#${gradientId})`}
        />
      </AreaChart>
    </ChartContainer>
  );
}
